import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  FormLabel,
  Grid,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  Message as MessageIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { zonasAPI, serviciosAPI } from '../services/api';
import MapLocationPicker from '../components/MapLocationPicker';
import MapPolygonEditor from '../components/MapPolygonEditor';

const DIAS_SEMANA = [
  { id: 1, label: 'L', nombre: 'Lunes' },
  { id: 2, label: 'M', nombre: 'Martes' },
  { id: 3, label: 'X', nombre: 'Miércoles' },
  { id: 4, label: 'J', nombre: 'Jueves' },
  { id: 5, label: 'V', nombre: 'Viernes' },
  { id: 6, label: 'S', nombre: 'Sábado' },
  { id: 7, label: 'D', nombre: 'Domingo' },
];

export default function Zonas() {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingZona, setEditingZona] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, zona: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [mapPicker, setMapPicker] = useState({ open: false, type: null });
  const [polygonEditor, setPolygonEditor] = useState({ open: false, zona: null, limites: [] });
  const [diasSeleccionados, setDiasSeleccionados] = useState([]);
  const [mensajesDialog, setMensajesDialog] = useState({ open: false, zona: null });
  const [mensajeForm, setMensajeForm] = useState({ titulo: '', mensaje: '' });

  const [formData, setFormData] = useState({
    IdZona: '',
    IdServicio: '',
    NombreZona: '',
    DiasRecoleccion: '',
    HoraInicioRetiro: '08:00:00',
    LimiteAvisoDesde: 0,
    LimiteAvisoHasta: 24,
    CalculoItinerarioAutomatico: 0,
    Lat_Origen: '',
    Lng_Origen: '',
    Lat_Destino: '',
    Lng_Destino: '',
  });

  const [errors, setErrors] = useState({});

  // Query para zonas
  const { data: zonas = [], isLoading: loadingZonas, error: zonasError } = useQuery({
    queryKey: ['zonas'],
    queryFn: async () => {
      const response = await zonasAPI.listar();
      return response.data.data;
    },
    onError: (error) => {
      console.error('Error loading zonas:', error);
      showSnackbar('Error al cargar zonas', 'error');
    },
  });

  // Query para servicios
  const { data: servicios = [] } = useQuery({
    queryKey: ['servicios'],
    queryFn: async () => {
      const response = await serviciosAPI.listar();
      return response.data.data;
    },
  });

  // Mutation para crear zona
  const createMutation = useMutation({
    mutationFn: zonasAPI.crear,
    onSuccess: () => {
      queryClient.invalidateQueries(['zonas']);
      handleCloseDialog();
      showSnackbar('Zona creada exitosamente', 'success');
    },
    onError: (error) => {
      const message = error.response?.data?.error?.message || 'Error al crear zona';
      showSnackbar(message, 'error');
    },
  });

  // Mutation para actualizar zona
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => zonasAPI.actualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['zonas']);
      handleCloseDialog();
      showSnackbar('Zona actualizada exitosamente', 'success');
    },
    onError: (error) => {
      const message = error.response?.data?.error?.message || 'Error al actualizar zona';
      showSnackbar(message, 'error');
    },
  });

  // Mutation para eliminar zona
  const deleteMutation = useMutation({
    mutationFn: zonasAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries(['zonas']);
      setDeleteDialog({ open: false, zona: null });
      showSnackbar('Zona eliminada exitosamente', 'success');
    },
    onError: (error) => {
      const message = error.response?.data?.error?.message || 'Error al eliminar zona';
      showSnackbar(message, 'error');
    },
  });

  // Query mensajes de zona
  const {
    data: mensajesData,
    isLoading: mensajesLoading,
    refetch: refetchMensajes,
  } = useQuery({
    queryKey: ['mensajes-zona', mensajesDialog.zona?.Id],
    queryFn: async () => {
      const response = await zonasAPI.obtenerMensajes(mensajesDialog.zona.Id);
      return response.data;
    },
    enabled: !!mensajesDialog.zona && mensajesDialog.open,
  });

  // Mutation enviar mensaje a zona
  const enviarMensajeMutation = useMutation({
    mutationFn: ({ id, data }) => zonasAPI.enviarMensaje(id, data),
    onSuccess: (res) => {
      refetchMensajes();
      setMensajeForm({ titulo: '', mensaje: '' });
      const d = res.data;
      const txt = d.sinPoligono
        ? 'Mensaje enviado (la zona no tiene polígono definido, no se registraron destinatarios)'
        : `Mensaje enviado a ${d.contribuyentesNotificados} contribuyente(s) en la zona`;
      showSnackbar(txt, d.sinPoligono ? 'warning' : 'success');
    },
    onError: (err) => {
      showSnackbar(err.response?.data?.error?.message || 'Error al enviar mensaje', 'error');
    },
  });

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (zona = null) => {
    if (zona) {
      setEditingZona(zona);

      // Parsear días de recolección
      const dias = zona.DiasRecoleccion ? zona.DiasRecoleccion.split(',').map(d => parseInt(d.trim())) : [];
      setDiasSeleccionados(dias);

      setFormData({
        IdZona: zona.IdZona || '',
        IdServicio: zona.IdServicio || '',
        NombreZona: zona.NombreZona || '',
        DiasRecoleccion: zona.DiasRecoleccion || '',
        HoraInicioRetiro: zona.HoraInicioRetiro || '08:00:00',
        LimiteAvisoDesde: zona.LimiteAvisoDesde || 0,
        LimiteAvisoHasta: zona.LimiteAvisoHasta || 24,
        CalculoItinerarioAutomatico: zona.CalculoItinerarioAutomatico || 0,
        Lat_Origen: zona.Lat_Origen || '',
        Lng_Origen: zona.Lng_Origen || '',
        Lat_Destino: zona.Lat_Destino || '',
        Lng_Destino: zona.Lng_Destino || '',
      });
    } else {
      setEditingZona(null);
      setDiasSeleccionados([]);
      setFormData({
        IdZona: '',
        IdServicio: '',
        NombreZona: '',
        DiasRecoleccion: '',
        HoraInicioRetiro: '08:00:00',
        LimiteAvisoDesde: 0,
        LimiteAvisoHasta: 24,
        CalculoItinerarioAutomatico: 0,
        Lat_Origen: '',
        Lng_Origen: '',
        Lat_Destino: '',
        Lng_Destino: '',
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingZona(null);
    setErrors({});
    setDiasSeleccionados([]);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleDiaChange = (diaId) => {
    setDiasSeleccionados(prev => {
      const newDias = prev.includes(diaId)
        ? prev.filter(d => d !== diaId)
        : [...prev, diaId].sort((a, b) => a - b);

      // Actualizar formData.DiasRecoleccion
      const diasString = newDias.join(',');
      setFormData(prevForm => ({ ...prevForm, DiasRecoleccion: diasString }));

      return newDias;
    });

    if (errors.DiasRecoleccion) {
      setErrors(prev => ({ ...prev, DiasRecoleccion: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.IdZona || String(formData.IdZona).trim() === '') {
      newErrors.IdZona = 'ID de zona es requerido';
    }
    if (!formData.IdServicio) {
      newErrors.IdServicio = 'Servicio es requerido';
    }
    if (!formData.NombreZona || String(formData.NombreZona).trim() === '') {
      newErrors.NombreZona = 'Nombre de zona es requerido';
    }
    if (diasSeleccionados.length === 0) {
      newErrors.DiasRecoleccion = 'Seleccione al menos un día de recolección';
    }
    if (!formData.HoraInicioRetiro) {
      newErrors.HoraInicioRetiro = 'Hora de inicio es requerida';
    }
    if (formData.LimiteAvisoDesde === '' || formData.LimiteAvisoDesde < 0) {
      newErrors.LimiteAvisoDesde = 'Límite desde debe ser mayor o igual a 0';
    }
    if (formData.LimiteAvisoHasta === '' || formData.LimiteAvisoHasta < 0) {
      newErrors.LimiteAvisoHasta = 'Límite hasta debe ser mayor o igual a 0';
    }
    if (!formData.Lat_Origen || !formData.Lng_Origen) {
      newErrors.Origen = 'Punto de origen es requerido';
    }
    if (!formData.Lat_Destino || !formData.Lng_Destino) {
      newErrors.Destino = 'Punto de destino es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      showSnackbar('Por favor complete todos los campos requeridos', 'error');
      return;
    }

    const dataToSend = {
      ...formData,
      LimiteAvisoDesde: parseInt(formData.LimiteAvisoDesde),
      LimiteAvisoHasta: parseInt(formData.LimiteAvisoHasta),
      CalculoItinerarioAutomatico: formData.CalculoItinerarioAutomatico ? 1 : 0,
      Lat_Origen: parseFloat(formData.Lat_Origen),
      Lng_Origen: parseFloat(formData.Lng_Origen),
      Lat_Destino: parseFloat(formData.Lat_Destino),
      Lng_Destino: parseFloat(formData.Lng_Destino),
    };

    if (editingZona) {
      updateMutation.mutate({ id: editingZona.Id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleDeleteClick = async (zona) => {
    try {
      const response = await zonasAPI.verificarEliminacion(zona.Id);
      const { canDelete, message } = response.data.data;

      if (!canDelete) {
        showSnackbar(message, 'warning');
        return;
      }

      setDeleteDialog({ open: true, zona });
    } catch (error) {
      showSnackbar('Error al verificar eliminación', 'error');
    }
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.zona) {
      deleteMutation.mutate(deleteDialog.zona.Id);
    }
  };

  const handleOpenMapPicker = (type) => {
    setMapPicker({ open: true, type });
  };

  const handleCloseMapPicker = () => {
    setMapPicker({ open: false, type: null });
  };

  const handleLocationSelected = (location) => {
    if (mapPicker.type === 'origen') {
      handleChange('Lat_Origen', location.lat);
      handleChange('Lng_Origen', location.lng);
      if (errors.Origen) {
        setErrors(prev => ({ ...prev, Origen: null }));
      }
    } else if (mapPicker.type === 'destino') {
      handleChange('Lat_Destino', location.lat);
      handleChange('Lng_Destino', location.lng);
      if (errors.Destino) {
        setErrors(prev => ({ ...prev, Destino: null }));
      }
    }
    handleCloseMapPicker();
  };

  const handleOpenMensajes = (zona) => {
    setMensajeForm({ titulo: '', mensaje: '' });
    setMensajesDialog({ open: true, zona });
  };

  const handleCloseMensajes = () => {
    setMensajesDialog({ open: false, zona: null });
  };

  const handleEnviarMensaje = () => {
    if (!mensajeForm.titulo.trim() || !mensajeForm.mensaje.trim()) {
      showSnackbar('Título y mensaje son requeridos', 'warning');
      return;
    }
    enviarMensajeMutation.mutate({
      id: mensajesDialog.zona.Id,
      data: { titulo: mensajeForm.titulo.trim(), mensaje: mensajeForm.mensaje.trim() },
    });
  };

  const handleOpenPolygonEditor = async (zona) => {
    try {
      // Cargar límites existentes si hay
      const response = await zonasAPI.obtenerLimites(zona.Id);
      const limites = response.data.data;

      setPolygonEditor({
        open: true,
        zona,
        limites,
      });
    } catch (error) {
      console.error('Error loading polygon limits:', error);
      setPolygonEditor({
        open: true,
        zona,
        limites: [],
      });
    }
  };

  const handleClosePolygonEditor = () => {
    setPolygonEditor({ open: false, zona: null, limites: [] });
  };

  const handleSavePolygon = async (limitesData) => {
    try {
      await zonasAPI.guardarLimites(polygonEditor.zona.Id, limitesData);
      showSnackbar('Límites guardados exitosamente', 'success');
      handleClosePolygonEditor();
    } catch (error) {
      console.error('Error saving polygon limits:', error);
      throw error;
    }
  };

  const getDiasBadges = (diasString) => {
    if (!diasString) return null;

    const dias = diasString.split(',').map(d => parseInt(d.trim()));
    return dias.map(diaId => {
      const dia = DIAS_SEMANA.find(d => d.id === diaId);
      return dia ? (
        <Chip
          key={diaId}
          label={dia.label}
          size="small"
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      ) : null;
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestión de Zonas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Zona
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID Zona</TableCell>
              <TableCell>Servicio</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Días Recolección</TableCell>
              <TableCell>Hora Inicio</TableCell>
              <TableCell align="center">Límites</TableCell>
              <TableCell align="center">Recolecciones</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingZonas ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : zonas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay zonas registradas
                </TableCell>
              </TableRow>
            ) : (
              zonas.map((zona) => (
                <TableRow key={zona.Id}>
                  <TableCell>{zona.IdZona}</TableCell>
                  <TableCell>{zona.NombreServicio || '-'}</TableCell>
                  <TableCell>{zona.NombreZona}</TableCell>
                  <TableCell>{getDiasBadges(zona.DiasRecoleccion)}</TableCell>
                  <TableCell>{zona.HoraInicioRetiro}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPolygonEditor(zona)}
                      color="primary"
                      title="Editar límites del polígono"
                    >
                      <MapIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">{zona.Qty_items || 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Enviar mensaje a la zona">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenMensajes(zona)}
                        color="secondary"
                      >
                        <MessageIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(zona)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(zona)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar zona */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingZona ? 'Editar Zona' : 'Nueva Zona'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID Zona"
                value={formData.IdZona}
                onChange={(e) => handleChange('IdZona', e.target.value)}
                error={!!errors.IdZona}
                helperText={errors.IdZona}
                disabled={!!editingZona}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.IdServicio} required>
                <InputLabel>Servicio</InputLabel>
                <Select
                  value={formData.IdServicio}
                  onChange={(e) => handleChange('IdServicio', e.target.value)}
                  label="Servicio"
                  disabled={!!editingZona}
                >
                  {servicios.map((servicio) => (
                    <MenuItem key={servicio.IdServicio} value={servicio.IdServicio}>
                      {servicio.NombreServicio}
                    </MenuItem>
                  ))}
                </Select>
                {errors.IdServicio && (
                  <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                    {errors.IdServicio}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de Zona"
                value={formData.NombreZona}
                onChange={(e) => handleChange('NombreZona', e.target.value)}
                error={!!errors.NombreZona}
                helperText={errors.NombreZona}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel component="legend" error={!!errors.DiasRecoleccion}>
                Días de Recolección *
              </FormLabel>
              <FormGroup row>
                {DIAS_SEMANA.map((dia) => (
                  <FormControlLabel
                    key={dia.id}
                    control={
                      <Checkbox
                        checked={diasSeleccionados.includes(dia.id)}
                        onChange={() => handleDiaChange(dia.id)}
                      />
                    }
                    label={dia.nombre}
                  />
                ))}
              </FormGroup>
              {errors.DiasRecoleccion && (
                <Typography variant="caption" color="error">
                  {errors.DiasRecoleccion}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Hora Inicio Retiro"
                type="time"
                value={formData.HoraInicioRetiro ? formData.HoraInicioRetiro.substring(0, 5) : '08:00'}
                onChange={(e) => handleChange('HoraInicioRetiro', e.target.value + ':00')}
                error={!!errors.HoraInicioRetiro}
                helperText={errors.HoraInicioRetiro}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Límite Aviso Retiro Desde (minutos antes)"
                type="number"
                value={formData.LimiteAvisoDesde}
                onChange={(e) => handleChange('LimiteAvisoDesde', e.target.value)}
                error={!!errors.LimiteAvisoDesde}
                helperText={errors.LimiteAvisoDesde}
                inputProps={{ min: 0 }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Límite Aviso Retiro Hasta (minutos antes)"
                type="number"
                value={formData.LimiteAvisoHasta}
                onChange={(e) => handleChange('LimiteAvisoHasta', e.target.value)}
                error={!!errors.LimiteAvisoHasta}
                helperText={errors.LimiteAvisoHasta}
                inputProps={{ min: 0 }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.CalculoItinerarioAutomatico === 1}
                    onChange={(e) => handleChange('CalculoItinerarioAutomatico', e.target.checked ? 1 : 0)}
                  />
                }
                label="Cálculo de Itinerario Automático"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Punto de Origen {errors.Origen && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Latitud Origen"
                type="number"
                value={formData.Lat_Origen}
                onChange={(e) => handleChange('Lat_Origen', e.target.value)}
                inputProps={{ step: 'any' }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Longitud Origen"
                type="number"
                value={formData.Lng_Origen}
                onChange={(e) => handleChange('Lng_Origen', e.target.value)}
                inputProps={{ step: 'any' }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <IconButton
                color="primary"
                onClick={() => handleOpenMapPicker('origen')}
                sx={{ mt: 0.5 }}
              >
                <LocationIcon />
              </IconButton>
            </Grid>

            {errors.Origen && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  {errors.Origen}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Punto de Destino {errors.Destino && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Latitud Destino"
                type="number"
                value={formData.Lat_Destino}
                onChange={(e) => handleChange('Lat_Destino', e.target.value)}
                inputProps={{ step: 'any' }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Longitud Destino"
                type="number"
                value={formData.Lng_Destino}
                onChange={(e) => handleChange('Lng_Destino', e.target.value)}
                inputProps={{ step: 'any' }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <IconButton
                color="primary"
                onClick={() => handleOpenMapPicker('destino')}
                sx={{ mt: 0.5 }}
              >
                <LocationIcon />
              </IconButton>
            </Grid>

            {errors.Destino && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  {errors.Destino}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={24} />
            ) : editingZona ? (
              'Actualizar'
            ) : (
              'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, zona: null })}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar la zona "{deleteDialog.zona?.NombreZona}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, zona: null })}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog mensajes de zona */}
      <Dialog open={mensajesDialog.open} onClose={handleCloseMensajes} maxWidth="md" fullWidth>
        <DialogTitle>
          Mensajes — {mensajesDialog.zona?.NombreZona}
          <Typography variant="caption" display="block" color="text.secondary">
            {mensajesData?.topic && `Topic: ${mensajesData.topic}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Formulario de envío */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Nuevo mensaje a toda la zona
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Título"
                  value={mensajeForm.titulo}
                  onChange={(e) => setMensajeForm((p) => ({ ...p, titulo: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mensaje"
                  multiline
                  rows={2}
                  value={mensajeForm.mensaje}
                  onChange={(e) => setMensajeForm((p) => ({ ...p, mensaje: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleEnviarMensaje}
                  disabled={enviarMensajeMutation.isPending}
                >
                  {enviarMensajeMutation.isPending ? 'Enviando...' : 'Enviar a zona'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Historial */}
          <Typography variant="subtitle2" gutterBottom>
            Historial de mensajes enviados
          </Typography>
          {mensajesLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : !mensajesData?.data?.length ? (
            <Typography variant="body2" color="text.secondary" align="center" py={2}>
              No hay mensajes enviados a esta zona
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Mensaje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mensajesData.data.map((msg) => (
                    <TableRow key={msg.Id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(msg.Fecha).toLocaleString('es-AR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell>{msg.Titulo}</TableCell>
                      <TableCell>{msg.Mensaje}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMensajes}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Map Picker */}
      <MapLocationPicker
        open={mapPicker.open}
        onClose={handleCloseMapPicker}
        onSelectLocation={handleLocationSelected}
        initialLat={mapPicker.type === 'origen' ? formData.Lat_Origen : formData.Lat_Destino}
        initialLng={mapPicker.type === 'origen' ? formData.Lng_Origen : formData.Lng_Destino}
        title={mapPicker.type === 'origen' ? 'Seleccionar Punto de Origen' : 'Seleccionar Punto de Destino'}
      />

      {/* Polygon Editor */}
      <MapPolygonEditor
        open={polygonEditor.open}
        onClose={handleClosePolygonEditor}
        zonaId={polygonEditor.zona ? `${polygonEditor.zona.IdZona} - ${polygonEditor.zona.NombreZona}` : null}
        limites={polygonEditor.limites}
        onSave={handleSavePolygon}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
